import { act, cleanup, render, screen } from '@testing-library/react'
import type { RolodexDocumentEntry } from 'features/files/types'
import { EmbeddedDocumentPanel } from 'features/libra/document/EmbeddedDocumentPanel'
import {
  type LibraDocumentContextValue,
  LibraDocumentProvider,
  useLibraDocument,
} from 'features/libra/document/LibraDocumentContext'
import {
  type InspectionArea,
  inspectionAreaSchema,
  planningAreasConfigSchema,
  planningStrategiesSchema,
} from 'features/libra/inspectionPlanning/planningTypes'
import { techniqueCatalogueSchema } from 'features/libra/inspectionPlanning/techniqueTypes'
import { wrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import type { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { zoneParamsSchema } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { StepValidationProvider } from 'features/libra/StepValidationContext'
import type { TicketAccessContextType } from 'features/libra/ticket/TicketAccessContext'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import type { RolodexEntryDispenserType } from 'features/rolodex/rolodexEntryDispenser'
import { type PropsWithChildren } from 'react'
import { ThemeProvider } from 'styled-components'
import { GeckoTheme } from 'styles/GeckoTheme'
import { GeckoMeasurementSystem, RolodexEntryType } from 'types'
import { afterEach, beforeEach, expect, vi } from 'vitest'

import { Component } from './NiiInspectionAreas'
import {
  imageProbes,
  loadMarkupSurface,
  resetMarkupProbes,
  resizeMarkupViewport,
  stubMarkupProbeGlobals,
} from './zoneAnalysis/testing/niiEditorTestUtils'

type DocumentSnapshot = Pick<
  LibraDocumentContextValue,
  'selectedDocumentId' | 'drawingHost' | 'drawingTarget'
>

const integration = vi.hoisted(() => {
  const state: {
    fieldValues: FieldValues
    globalContext: LibraWorkflow['globalContext']
    documents: RolodexDocumentEntry[]
    reports: RolodexEntryDispenserType['reports']
    document?: DocumentSnapshot
    failNextSave: boolean
    loading: boolean
    notify?: () => void
    access: Pick<
      TicketAccessContextType,
      'fieldValues' | 'readOnly' | 'isLoading' | 'setFieldValue'
    >
  } = {
    fieldValues: {},
    globalContext: {},
    documents: [],
    reports: [],
    document: undefined,
    failNextSave: false,
    loading: false,
    notify: undefined,
    access: {
      fieldValues: {},
      readOnly: false,
      get isLoading() {
        return state.loading
      },
      setFieldValue: async () => {},
    },
  }
  state.access = {
    get fieldValues() {
      return state.fieldValues
    },
    readOnly: false,
    get isLoading() {
      return state.loading
    },
    setFieldValue: async (fieldId, value) => {
      if (state.failNextSave) {
        state.failNextSave = false
        throw new Error('save failed')
      }
      state.fieldValues = { ...state.fieldValues, [fieldId]: value }
      state.notify?.()
    },
  }
  return state
})

export const getIntegration = () => integration

vi.mock('features/libra/ticket/TicketAccessContext', () => ({
  useTicketAccess: () => integration.access,
}))
vi.mock('features/libra/WorkflowContext', () => ({
  useWorkflow: () => ({ globalContext: integration.globalContext }),
}))
vi.mock('features/libra/ontology/useOntologyFields', () => ({
  useActiveScopeOntologyFields: () => ({
    ontologyFields: {
      inspection_areas: {
        category: 'input',
        items: { properties: { title: { name: 'Area label' } } },
      },
    },
  }),
}))
vi.mock('features/locale', () => ({
  useGeckoLocale: () => ({ measurementSystem: GeckoMeasurementSystem.Metric }),
}))
vi.mock('features/rolodex', () => ({
  useRolodexEntries: () => ({
    reports: integration.reports,
    forceRefresh: vi.fn(),
  }),
}))
vi.mock('features/fde/rolodexAPI', () => ({
  useRolodexEdit: () => ({ rolodexPatch: vi.fn() }),
}))
vi.mock('features/orgAndSiteSourceOfTruth/orgSiteInterface', () => ({
  useSelectedOrgAndSiteInterface: () => ({
    getOrganizationId: () => undefined,
    getSiteId: () => undefined,
  }),
}))
vi.mock('react-resize-detector', () => ({
  useResizeDetector: () => ({ ref: vi.fn(), height: 600 }),
}))
vi.mock('features/files', () => ({
  DocumentPreview: () => <div />,
  isImageDocument: (document: RolodexDocumentEntry | null) =>
    Boolean(document?.filename?.endsWith('.png')),
  isPdfDocument: () => false,
  useDocumentSignedUrls: (document: RolodexDocumentEntry | null) => ({
    signedUrl: document ? `/${document.entry_id}.png` : undefined,
    filePath: document ? `/${document.entry_id}.png` : undefined,
  }),
  useLinkedDocuments: () => ({
    documents: integration.documents,
    isLoading: false,
    forceRefresh: vi.fn(),
  }),
}))

const config = planningAreasConfigSchema.parse({
  sourceZonesField: 'source_zones',
  recommendationsField: 'inspection_recommendations',
  areasField: 'inspection_areas',
  techniqueCatalogueContextKey: 'techniques',
  methodPlanning: { requireEstimate: true, requiredFields: ['instructions'] },
})
const strategies = planningStrategiesSchema.parse({
  A: { detailedDrawing: true },
  B: { detailedDrawing: true },
  C: { detailedDrawing: false, plannedCoveragePercent: 100 },
})
const catalogue = techniqueCatalogueSchema.parse({
  degradationTypes: [{ id: 'loss', label: 'Loss', techniqueIds: ['ut'] }],
  techniques: [
    {
      id: 'ut',
      label: 'UT',
      scopeType: 'area',
      description: 'Ultrasonic testing',
      pod: null,
    },
  ],
  units: { area: 'm²', length: 'm', count: 'readings' },
})
export const sourceZone = zoneParamsSchema.parse({
  id: 'same',
  title: 'Shell',
  zone_reference_image_id: 'image-a',
  drawing_bounds: [{ x_start: 0, y_start: 0, x_end: 1400, y_end: 700 }],
  nii_strategy: { strategy_type_calculated: 'C' },
  coverage_calculation: { required_coverage_percent: 100 },
})
export const historicalArea = inspectionAreaSchema.parse({
  id: 'same',
  sourceZoneId: 'same',
  title: 'Saved same',
  scope: 'location',
  zone_reference_image_id: 'image-history',
  drawing_bounds: { x_start: 200, y_start: 100, x_end: 500, y_end: 300 },
  degradationTypeId: 'loss',
  methodAssignments: [
    {
      id: 'ut-same',
      techniqueId: 'ut',
      procedure: 'UT-01',
      instructions: 'Scan the band',
      estimatedScope: { num: 20, unit: 'm²' },
    },
  ],
})
export const peerArea = inspectionAreaSchema.parse({
  id: 'peer',
  sourceZoneId: 'same',
  title: 'Saved peer',
  scope: 'location',
  zone_reference_image_id: 'image-a',
  drawing_bounds: { x_start: 700, y_start: 100, x_end: 900, y_end: 300 },
  methodAssignments: [],
})

export const fieldValues = (
  areas: InspectionArea[] = [historicalArea, peerArea]
) => ({
  source_zones: wrapFieldValue([sourceZone]),
  inspection_areas: wrapFieldValue(areas),
  inspection_recommendations: wrapFieldValue({ per_zone: [], asset_level: [] }),
})
const documents: RolodexDocumentEntry[] = ['image-a', 'image-history'].map(
  (id) => ({
    type: RolodexEntryType.REPORT,
    entry_id: id,
    filename: `${id}.png`,
    visible_name: id,
    file_type: 'png',
    fields: {},
    categories: ['Zone Reference Images'],
    tags: { unit: 'unit-1' },
  })
)

const DocumentProbe = () => {
  const {
    selectedDocumentId,
    drawingHost,
    drawingTarget,
    setSelectedDocumentId,
  } = useLibraDocument()
  integration.document = { selectedDocumentId, drawingHost, drawingTarget }
  return (
    <button onClick={() => setSelectedDocumentId('image-history')}>
      Open history fixture
    </button>
  )
}
export const readDocument = (): DocumentSnapshot => {
  if (!integration.document)
    throw new Error('The document drawing context is not mounted')
  return integration.document
}
export const expectDrawingArmed = () =>
  expect(
    screen.getAllByRole('button', { name: 'Cancel drawing' }).length
  ).toBeGreaterThan(0)
export const expectDrawingIdle = (label = 'Add optional area') =>
  expect(screen.getByRole('button', { name: label })).toBeTruthy()
export const annotationShape = (regionId: string) => {
  const shape = document.querySelector<SVGGraphicsElement>(
    `svg[data-annotation-id="${regionId}"] [data-annotation-shape="true"]`
  )
  if (!shape) throw new Error(`Expected the ${regionId} outline`)
  return shape
}
export const annotationBounds = (regionId: string) => {
  const shape = annotationShape(regionId)
  const x = Number(shape.getAttribute('x'))
  const y = Number(shape.getAttribute('y'))
  const width = Number(shape.getAttribute('width'))
  const height = Number(shape.getAttribute('height'))
  return {
    x_start: x * 1600,
    y_start: y * 800,
    x_end: (x + width) * 1600,
    y_end: (y + height) * 800,
  }
}
export const areaCard = (title: string) => {
  const titleNode = screen
    .getAllByText(title)
    .find((element) => element.classList.contains('ant-card-head-title'))
  const card = titleNode?.closest('.ant-card')
  if (!(card instanceof HTMLElement))
    throw new Error(`Expected the ${title} area card`)
  return card
}
export const areaTitleField = (card: HTMLElement, areaId: string) => {
  const field = card.querySelector<HTMLTextAreaElement>(
    `textarea#${areaId}-title`
  )
  if (!field) throw new Error(`Expected the ${areaId} title field`)
  return field
}
const wrapper = ({ children }: PropsWithChildren) => (
  <ThemeProvider theme={GeckoTheme}>
    <LibraDocumentProvider unitId='unit-1'>
      <StepValidationProvider>{children}</StepValidationProvider>
    </LibraDocumentProvider>
  </ThemeProvider>
)
export const renderOwner = (options?: {
  areas?: InspectionArea[]
  readonly?: boolean
}) => {
  if (options?.areas) integration.fieldValues = fieldValues(options.areas)
  const view = render(
    <>
      <Component
        config={config}
        name='Inspection areas'
        readonly={options?.readonly}
        setValidationResult={vi.fn()}
      />
      <EmbeddedDocumentPanel />
      <DocumentProbe />
    </>,
    { wrapper }
  )
  integration.notify = () =>
    view.rerender(
      <>
        <Component
          config={config}
          name='Inspection areas'
          readonly={options?.readonly}
          setValidationResult={vi.fn()}
        />
        <EmbeddedDocumentPanel />
        <DocumentProbe />
      </>
    )
  return view
}

beforeEach(() => {
  integration.fieldValues = fieldValues()
  integration.globalContext = {
    inspectionPlanningStrategies: strategies,
    techniques: catalogue,
  }
  integration.documents = documents
  integration.reports = []
  integration.failNextSave = false
  integration.loading = false
  integration.notify = undefined
  integration.document = undefined
  resetMarkupProbes()
  const nativeTitleQuerySelector = SVGTitleElement.prototype.querySelector
  vi.spyOn(SVGTitleElement.prototype, 'querySelector').mockImplementation(
    function (this: SVGTitleElement, selector: string) {
      // shortcut: Ant Form/InputNumber/Card :has rules cannot match text-only SVG titles; remove when jsdom handles Ant :has with React colon IDs.
      const antFormSwitchOrRateRule =
        selector.includes('.ant-switch:only-child') &&
        selector.includes('.ant-rate:only-child')
      const antInputNumberSelectRule = selector === ':scope .ant-select'
      const antCardHeadRule = /^:scope\s*>\s*\.ant-card-head$/.test(selector)
      if (
        antFormSwitchOrRateRule ||
        antInputNumberSelectRule ||
        antCardHeadRule
      )
        return null
      return nativeTitleQuerySelector.call(this, selector)
    }
  )
  const nativeTextAreaQuerySelector =
    HTMLTextAreaElement.prototype.querySelector
  vi.spyOn(HTMLTextAreaElement.prototype, 'querySelector').mockImplementation(
    function (this: HTMLTextAreaElement, selector: string) {
      // shortcut: Ant Form's :has rule can produce an invalid selector for React-generated IDs in jsdom.
      const antFormSwitchOrRateRule =
        selector.includes('.ant-switch:only-child') &&
        selector.includes('.ant-rate:only-child')
      const antInputNumberSelectRule = selector.includes('.ant-select')
      const antCardHeadRule = selector.includes('.ant-card-head')
      if (
        antFormSwitchOrRateRule ||
        antInputNumberSelectRule ||
        antCardHeadRule
      )
        return null
      return nativeTextAreaQuerySelector.call(this, selector)
    }
  )
  stubMarkupProbeGlobals()
  vi.stubGlobal('matchMedia', (query: string) => ({
    matches: false,
    media: query,
    onchange: null,
    addListener: vi.fn(),
    removeListener: vi.fn(),
    addEventListener: vi.fn(),
    removeEventListener: vi.fn(),
    dispatchEvent: vi.fn(),
  }))
  Object.defineProperty(HTMLElement.prototype, 'scrollIntoView', {
    configurable: true,
    value: vi.fn(),
  })
})
afterEach(() => {
  cleanup()
  vi.restoreAllMocks()
  vi.unstubAllGlobals()
})

export const loadMeasuredImage = (url: string, width = 1600, height = 800) => {
  const probe = imageProbes.findLast((image) => image.src === url)
  if (!probe) throw new Error(`No measurement started for ${url}`)
  act(() => probe.load(width, height))
}
export const loadEditorSurface = (width = 1600, height = 800) => {
  return loadMarkupSurface(width, height, 400, 200)
}
export const resizeEditorViewport = (
  surface: Element,
  width: number,
  height: number
) => {
  resizeMarkupViewport(surface, width, height)
}
